import { useState, useEffect } from 'react';
import img1 from '../images/why-us-img-1.jpeg';

const WhyUs = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://admin.takeaya.com/api/admin/reviews');
        
        if (!response.ok) {
          throw new Error('Failed to fetch reviews');
        }
        
        const responseData = await response.json();
        const reviewsData = responseData.data || [];
        setReviews(reviewsData);
        setError(null);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError(err.message);
        // Fallback to demo reviews
        setReviews([
          {
            id: 1,
            user_name: "Amal",
            location: "Riyadh, Saudi Arabia",
            details: "Takeaya transformed our vacation into a dream. Impeccable service and stunning homes that exceeded all expectations.",
            starts: "5"
          },
          {
            id: 2,
            user_name: "Kareem",
            location: "Dubai, UAE",
            details: "As an owner, I have never experienced such transparency and profitability. The attention to detail and communication is unparalleled.",
            starts: "5"
          },
          {
            id: 3,
            user_name: "Sara",
            location: "Kuwait City, Kuwait",
            details: "Outstanding property management with exceptional returns. The team's professionalism is truly remarkable.",
            starts: "5"
          },
          {
            id: 4,
            user_name: "Ahmed",
            location: "Doha, Qatar",
            details: "Perfect blend of luxury and comfort. Every detail was thoughtfully arranged for an unforgettable experience.",
            starts: "5"
          },
          {
            id: 5,
            user_name: "Layla",
            location: "Abu Dhabi, UAE",
            details: "Seamless booking process and world-class accommodations. Takeaya sets the gold standard in hospitality.",
            starts: "5"
          },
          {
            id: 6,
            user_name: "Omar",
            location: "Manama, Bahrain",
            details: "Exceeded every expectation. The property was immaculate and the service was absolutely flawless.",
            starts: "5"
          },
          {
            id: 7,
            user_name: "Fatima",
            location: "Muscat, Oman",
            details: "A truly premium experience from start to finish. The attention to detail is what sets Takeaya apart.",
            starts: "5"
          },
          {
            id: 8,
            user_name: "Hassan",
            location: "Jeddah, Saudi Arabia",
            details: "Professional, reliable, and consistently delivers exceptional results. Highly recommend their services.",
            starts: "5"
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    const stars = parseInt(rating) || 5;
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-3 h-3 ${i < stars ? 'text-yellow-400' : 'text-gray-300'}`}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
      </svg>
    ));
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden bg-gray-50">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-1/3 h-px bg-gradient-to-l from-amber-600 to-transparent opacity-30"></div>
        <div className="absolute top-0 left-0 w-1/3 h-px bg-gradient-to-r from-amber-600 to-transparent opacity-30"></div>
        <div className="absolute bottom-0 right-0 w-1/3 h-px bg-gradient-to-l from-amber-600 to-transparent opacity-30"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-amber-600 to-transparent opacity-30"></div>
      </div>

      {/* Decorative shapes */}
      <div className="absolute top-1/4 right-0 w-1/4 opacity-5">
        <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          <path
            fill="#A3582A"
            d="M31.7,-53.1C40.5,-45.5,46.9,-35.6,52.1,-25C57.3,-14.5,61.3,-3.3,60.2,7.4C59.1,18.1,53,28.2,44.7,35.8C36.5,43.4,26.1,48.5,15.4,52.4C4.7,56.3,-6.4,59,-17.2,57.5C-28,56,-38.4,50.3,-47.4,42C-56.4,33.7,-64,22.8,-67.5,10.5C-71.1,-1.8,-70.6,-15.6,-64.6,-26C-58.7,-36.5,-47.3,-43.7,-35.8,-49.9C-24.3,-56.1,-12.1,-61.4,-0.1,-61.3C12,-61.1,23.9,-60.6,31.7,-53.1Z"
            transform="translate(100 100)"
          />
        </svg>
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center mb-10 sm:mb-16 md:mb-20">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mb-4 sm:mb-6 relative">
            <div className="absolute inset-0 rounded-full border-2 border-amber-600/30"></div>
            <div className="absolute inset-3 rounded-full border border-amber-600/60"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
          </div>
          <h2 className="text-amber-600 text-xs sm:text-sm uppercase tracking-widest mb-2 sm:mb-3 text-center font-medium">
            Your Success, Our Priority
          </h2>
          <h3 className="text-3xl sm:text-4xl md:text-5xl text-amber-900 mb-4 sm:mb-6 text-center max-w-3xl mx-auto leading-tight px-2 font-serif">
            Why Choose Us?
          </h3>
          <div className="w-16 sm:w-24 h-px bg-amber-600 opacity-40 mb-4 sm:mb-6"></div>
          <p className="text-gray-600 text-center max-w-2xl mx-auto leading-relaxed text-sm sm:text-base">
            Experience stress-free property management with flexible payment options, 
            AI-powered optimization, and boutique-level service tailored to your needs.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 md:gap-10 mb-16 sm:mb-20 md:mb-24">
          {[
            {
              title: "Fit Out Options",
              description: "Choose from full upfront payment or a 6-month installment plan deducted from your rental income — stress-free furnishing that fits your budget.",
              icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2zm16 0v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2z",
            },
            {
              title: "Hands-Off Experience",
              description: "We handle everything — listings, guest check-ins, bookings, maintenance, and even furnishing.",
              icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
            },
            {
              title: "Smart Operations",
              description: "We use AI-powered pricing and automation tools to keep your calendar full and your income optimized.",
              icon: "M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
            },
            {
              title: "Owner Transparency",
              description: "Monthly reports, real-time dashboards, and direct communication. No guessing, no ghosting.",
              icon: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
            },
            {
              title: "Cleaning & Maintenance",
              description: "Partnered with trusted service providers to ensure your unit stays guest-ready at all times.",
              icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
            },
            {
              title: "Tailor Service & High Returns",
              description: "Whether you own one unit or ten, we offer boutique-level service that adapts to your needs. We work on a competitive 15% management fee with no hidden costs.",
              icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
            },
          ].map((benefit, index) => (
            <div key={index} className="group p-1">
              <div className="h-full bg-white border border-amber-600/10 p-4 sm:p-6 md:p-8 hover:shadow-xl transition-all duration-500 relative rounded-lg">
                {/* Hover effect corners */}
                <div className="absolute top-0 left-0 w-0 h-0 border-t-2 border-l-2 border-amber-600 opacity-0 group-hover:opacity-100 group-hover:w-8 sm:group-hover:w-12 group-hover:h-8 sm:group-hover:h-12 transition-all duration-500"></div>
                <div className="absolute bottom-0 right-0 w-0 h-0 border-b-2 border-r-2 border-amber-600 opacity-0 group-hover:opacity-100 group-hover:w-8 sm:group-hover:w-12 group-hover:h-8 sm:group-hover:h-12 transition-all duration-500"></div>

                {/* Icon */}
                <div className="mb-4 sm:mb-6 group-hover:translate-y-[-5px] transition-transform duration-500">
                  <div className="inline-flex items-center justify-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 border border-amber-600/30 rounded-full flex items-center justify-center relative">
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d={benefit.icon} />
                      </svg>
                    </div>
                    <div className="w-8 sm:w-12 h-px bg-amber-600/30 ml-3 sm:ml-4 group-hover:w-12 sm:group-hover:w-20 transition-all duration-500"></div>
                  </div>
                </div>

                {/* Content */}
                <h4 className="text-lg sm:text-xl text-amber-900 mb-2 sm:mb-3 font-serif">
                  {benefit.title}
                </h4>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="relative mb-16 sm:mb-20 md:mb-24 overflow-hidden rounded-lg sm:rounded-xl shadow-lg sm:shadow-2xl">
          <div className="absolute inset-0">
            <img
              src={img1}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-amber-900 to-amber-800 opacity-80"></div>
          </div>

          <div className="relative z-10 py-8 sm:py-12 md:py-16 px-4 sm:px-6 md:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-10">
              {[
                {
                  value: "95%",
                  label: "Average occupancy rate",
                  description: "Consistently maximizing property potential",
                },
                {
                  value: "14.6%",
                  label: "ROI in top locations",
                  description: "Exceptional returns for our property owners",
                },
                {
                  value: "2.6M+",
                  label: "Organic views from campaigns",
                  description: "Extensive reach through strategic marketing",
                },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <h4 className="font-serif text-4xl sm:text-5xl md:text-6xl text-white mb-2 sm:mb-3 drop-shadow-sm">
                    {stat.value}
                  </h4>
                  <div className="w-10 sm:w-12 h-px bg-white opacity-60 mx-auto mb-2 sm:mb-3"></div>
                  <p className="text-white text-base sm:text-lg font-serif font-medium mb-1 sm:mb-2">
                    {stat.label}
                  </p>
                  <p className="text-white/80 text-xs sm:text-sm">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div className="mb-16 sm:mb-20 md:mb-24">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h4 className="text-2xl sm:text-3xl text-amber-900 mb-2 sm:mb-3 font-serif">
              What Our Clients Say
            </h4>
            <div className="w-16 sm:w-20 h-px bg-amber-600 opacity-40 mx-auto"></div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
              <p className="mt-2 text-gray-500">Loading reviews...</p>
            </div>
          ) : (
            <div className="text-start grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {reviews.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-white p-4 sm:p-6 relative hover:shadow-xl transition-all duration-500 group border border-amber-600/10 rounded-lg"
                  style={{ minHeight: '200px' }}
                >
                  {/* Quote marks */}
                  <div className="absolute top-2 left-2 text-amber-600/20 text-2xl font-serif">"</div>
                  <div className="absolute bottom-2 right-2 text-amber-600/20 text-2xl font-serif rotate-180">"</div>

                  {/* Content */}
                  <div className="relative z-10 flex flex-col h-full">
                    <p className="text-start text-gray-600 text-sm leading-relaxed mb-4 flex-grow font-serif">
                      {testimonial.details}
                    </p>

                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-amber-600/10 rounded-full flex items-center justify-center text-amber-600 font-medium text-sm">
                          {testimonial.user_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-3">
                          <p className="text-amber-900 text-sm font-serif font-medium">
                            {testimonial.user_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {testimonial.location}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1">
                        {renderStars(testimonial.starts)}
                      </div>
                    </div>
                  </div>

                  {/* Hover effect */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-lg">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-600/0 via-amber-600 to-amber-600/0"></div>
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-600/0 via-amber-600 to-amber-600/0"></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;